import React, { useState } from 'react';
import axios from 'axios';
import { useUser } from '../Usercontext';
import config from '../config';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

const OnboardingPopover: React.FC = () => {
  const { setIsOnboarded } = useUser();
  const [software, setSoftware] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [canContact, setCanContact] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralSource, setReferralSource] = useState('');
  const [open, setOpen] = useState(true); // Opens the dialog immediately

  const softwareOptions = [
    'Salesforce',
    'Zoho',
    'Pipedrive',
    'Insightly',
    'QuickBooks',
    'FreshBooks',
    'Xero',
    'Wave',
    'Thryv',
    'ServiceTitan',
    'Housecall Pro',
    'Jobber',
    'FieldEdge',
    'Service Fusion',
    'Simpro',
    'ServiceMax',
    'Connecteam',
    'Custom Solution',
    'None',
    'Other'
  ];

  const referralSources = [
    'Google Search',
    'Reddit',
    'Facebook',
    'LinkedIn',
    'Industry Forums',
    'Referral from Friend/Colleague',
    'Industry Event',
    'Online Advertisement',
    'Other'
  ];

  // Updated regular expression for validating phone number starting with "+" and country code
  const phoneRegex = /^(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})$/; // Accepts formats like: 123-456-7890, 123.456.7890, 123 456 7890

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!software) {
      newErrors.software = 'Please select your software';
    }
    if (!teamSize) {
      newErrors.teamSize = 'Please select your team size';
    }
    if (!referralSource) {
      newErrors.referralSource = 'Please tell us how you found us';
    }
    if (phoneNumber && !phoneRegex.test(phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number (e.g., 123-456-7890)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const email = localStorage.getItem('email');
        if (!email) {
          toast.error('No email found. Please try logging in again.');
          return;
        }

        const response = await axios.post(
          `${config.apiBaseUrl}/onboarding/`,
          {
            software,
            team_size: teamSize,
            phone_number: phoneNumber,
            can_contact: canContact,
            referral_source: referralSource
          },
          {
            headers: {
              'X-User-Email': email,
            },
          }
        );

        const userSession = localStorage.getItem('userSession');
        if (userSession) {
          const userData = JSON.parse(userSession);
          userData.is_onboarded = response.data.is_onboarded;
          localStorage.setItem('userSession', JSON.stringify(userData));
        }
        
        toast.success('Thank you for completing the onboarding!');
        setIsOnboarded(true);
        setOpen(false); // Close dialog on success
      } catch (error) {
        console.error('Failed to submit onboarding form:', error);
        toast.error('Failed to save your preferences. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      toast.error('Please fill in all required fields.');
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Onboarding - Just a few questions before you get started</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal" disabled={isSubmitting} error={!!errors.referralSource}>
          <InputLabel>How did you find us?</InputLabel>
          <Select
            value={referralSource}
            onChange={(e) => {
              setReferralSource(e.target.value);
              setErrors({ ...errors, referralSource: '' });
            }}
            label="How did you find us?"
          >
            <MenuItem value="">Select...</MenuItem>
            {referralSources.map((source, index) => (
              <MenuItem key={index} value={source}>{source}</MenuItem>
            ))}
          </Select>
          {errors.referralSource && <span className="error-message">{errors.referralSource}</span>}
        </FormControl>

        <FormControl fullWidth margin="normal" disabled={isSubmitting} error={!!errors.software}>
          <InputLabel>What software do you use for making estimates and invoices?</InputLabel>
          <Select
            value={software}
            onChange={(e) => {
              setSoftware(e.target.value);
              setErrors({ ...errors, software: '' });
            }}
            label="What software do you use for making estimates and invoices?"
          >
            <MenuItem value="">Select...</MenuItem>
            {softwareOptions.map((option, index) => (
              <MenuItem key={index} value={option}>{option}</MenuItem>
            ))}
          </Select>
          {errors.software && <span className="error-message">{errors.software}</span>}
        </FormControl>

        <FormControl fullWidth margin="normal" disabled={isSubmitting} error={!!errors.teamSize}>
          <InputLabel>How large is your team?</InputLabel>
          <Select
            value={teamSize}
            onChange={(e) => {
              setTeamSize(e.target.value);
              setErrors({ ...errors, teamSize: '' });
            }}
            label="How large is your team?"
          >
            <MenuItem value="">Select...</MenuItem>
            <MenuItem value="1-5">1-5</MenuItem>
            <MenuItem value="6-10">6-10</MenuItem>
            <MenuItem value="11-20">11-20</MenuItem>
            <MenuItem value="21+">21+</MenuItem>
          </Select>
          {errors.teamSize && <span className="error-message">{errors.teamSize}</span>}
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Phone Number (optional)"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={isSubmitting}
          type="tel"
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber || "Format: 123-456-7890"}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={canContact}
              onChange={(e) => setCanContact(e.target.checked)}
              disabled={isSubmitting}
            />
          }
          label="Can we contact you to learn more about your needs and how we can serve you better?"
        />

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Submit'}
          </Button>
        </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingPopover;
